import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { getIsLoggedIn } from '../../publicSession/selectors';
import { restrictLoggedOut, LoggedOutReturnType } from '../../redux/loggedout';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import noop from '../noop';
import { yieldToMain } from '@wix/da-hooks/pkg/yieldToMain';

interface ReturnType {
  isLoggedIn: boolean;
  sendUserToJoinPage: (
    restrictedActionName: ReferralType,
    returnTo?: string,
    returnType?: LoggedOutReturnType
  ) => void;
  restrictToLoggedIn: (
    restrictedActionName: ReferralType,
    callback?: (...args: any[]) => void,
    returnTo?: string,
    returnType?: LoggedOutReturnType
  ) => any;
}

/** returns whether current user is logged in,
 *  a dispatched action that sends the user to the sisu login pages,
 *  and a function that will call a callback if logged in, send to login if not */
export function useRestrictToLoggedIn(): ReturnType {
  const isLoggedIn = useSelector(getIsLoggedIn);

  const dispatch = useDispatch();
  const sendUserToJoinPage = useCallback(
    (restrictedActionName: ReferralType, returnTo?: string, returnType?: LoggedOutReturnType) => {
      yieldToMain(() => {
        dispatch(restrictLoggedOut({ restrictedActionName, returnTo, returnType }));
      });
    },
    [dispatch]
  );

  const restrictToLoggedIn = useCallback(
    (
      restrictedActionName: ReferralType,
      callback: (...args: any[]) => void = noop,
      returnTo: string | undefined,
      returnType: LoggedOutReturnType | undefined
    ) =>
      (...args: any[]): void => {
        return !isLoggedIn
          ? sendUserToJoinPage(restrictedActionName, returnTo, returnType)
          : yieldToMain(() => {
              callback(...args);
            });
      },
    [isLoggedIn, sendUserToJoinPage]
  );

  return { isLoggedIn, sendUserToJoinPage, restrictToLoggedIn };
}
